import { DownloadOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button } from "antd";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { Bar, Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  getInsightsSampsOverTimeByOutlet,
  getInsightsStampTotDist,
} from "../../actions/acc_actions";

import Loading from "../../utils/Loading";

ChartJS.register(
  LineElement,
  BarElement,
  PointElement,
  LinearScale,
  TimeScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
);

const StampsChart = ({ dateFrom, dateTo, selectedSchema }) => {
  const { t } = useTranslation();
  const businessSlug = useSelector((state) => state.account.businessSlug);

  const { isLoading: stampsLoading, data: stampsData } = useQuery({
    queryKey: [
      "insightSampsOverTimeByOutlet",
      businessSlug,
      selectedSchema,
      dateFrom,
      dateTo,
    ],
    queryFn: () =>
      getInsightsSampsOverTimeByOutlet({
        to: dateTo,
        from: dateFrom,
        refresh: false,
        loyalty_scheme_slug: selectedSchema,
      }),
    enabled: !!dateTo && !!dateFrom && !!businessSlug && !!selectedSchema,
    keepPreviousData: true,
  });

  const { isLoading: stampsTotLoading, data: stampsTotData } = useQuery({
    queryKey: [
      "insightStampTotDist",
      businessSlug,
      selectedSchema,
      dateFrom,
      dateTo,
    ],
    queryFn: () =>
      getInsightsStampTotDist({
        refresh: false,
        loyalty_scheme_slug: selectedSchema,
      }),
    enabled: !!dateTo && !!dateFrom && !!businessSlug && !!selectedSchema,
    keepPreviousData: true,
  });

  if (stampsLoading || stampsTotLoading) {
    return <Loading />;
  }

  const series = stampsData?.body?.series || [];
  const distChartData = stampsTotData?.body?.data || [];

  if (!series.length && !distChartData.length) {
    return <div />;
  }

  const allDates = series?.[0]?.data?.map((d) => d.event_date) || [];

  const getColor = (index) => {
    const hue = (index * 137.508) % 360;
    return `hsl(${hue},70%,50%)`;
  };

  // -----------------------
  // CSV DOWNLOAD HELPER
  // -----------------------
  const downloadCSV = (filename, headers, rows) => {
    const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  };

  // Export line chart data
  const downloadStampsTime = () => {
    const rows = [];

    series.forEach((outlet) => {
      outlet.data.forEach((row) => {
        rows.push([row.event_date, outlet.outlet_name, row.number_of_stamps]);
      });
    });

    downloadCSV(
      `stamps-over-time-${dateFrom}-to-${dateTo}.csv`,
      ["Date", "Outlet", "Number Of Stamps"],
      rows,
    );
  };

  // Export distribution chart data
  const downloadDistribution = () => {
    const rows = distChartData.map((item) => [
      item.stamp_total,
      item.number_of_customers,
    ]);

    downloadCSV(
      `stamp-distribution-${dateFrom}-to-${dateTo}.csv`,
      ["Stamp Total", "Number Of Customers"],
      rows,
    );
  };

  const datasets = series.map((outlet, idx) => ({
    label: outlet.outlet_name,
    data: outlet.data.map((d) => d.number_of_stamps),
    borderColor: getColor(idx),
    backgroundColor: getColor(idx) + "33",
    fill: false,
    tension: 0.4,
  }));

  const timeChartConfig = {
    labels: allDates,
    datasets,
  };

  const timeChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "time",
        time: {
          unit: "day",
          displayFormats: {
            day: "MMM dd",
          },
        },
        min: dateFrom,
        max: dateTo,
        title: {
          display: true,
          text: t("date"),
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: t("number_of_stamps"),
        },
      },
    },
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
  };

  const distChartConfig = {
    labels: distChartData.map((item) => item.stamp_total.toString()),
    datasets: [
      {
        label: t("number_of_customers"),
        data: distChartData.map((item) => item.number_of_customers),
        backgroundColor: "rgba(75,192,192,.6)",
        borderColor: "rgba(75,192,192,1)",
        borderWidth: 1,
      },
    ],
  };

  const distChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "category",
        title: {
          display: true,
          text: t("stamp_total"),
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: t("number_of_customers"),
        },
      },
    },
    plugins: {
      legend: {
        position: "top",
      },
    },
  };

  return (
    <div className="chart">
      <h3>{t("stamps_rewards_data")}</h3>

      <div className="charts-wrapper">
        {/* Chart 1 */}
        <div className="chart-item">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <h4 style={{ margin: 0 }}>{t("stamps_over_time_by_outlet")}</h4>

            <Button
              size="medium"
              icon={<DownloadOutlined />}
              onClick={downloadStampsTime}
            >
              {t("download_data")}
            </Button>
          </div>

          <div className="chart-container">
            <Line data={timeChartConfig} options={timeChartOptions} />
          </div>
        </div>

        {/* Chart 2 */}
        <div className="chart-item">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <h4 style={{ margin: 0 }}>{t("stamp_total_distribution")}</h4>

            <Button
              size="medium"
              icon={<DownloadOutlined />}
              onClick={downloadDistribution}
            >
              {t("download_data")}
            </Button>
          </div>

          <div className="chart-container">
            <Bar data={distChartConfig} options={distChartOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StampsChart;
